import './RegionRow.css';
import type { JSX } from 'preact';
import { useEffect, useId } from 'preact/hooks';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { Bar } from '../../components/Bar';
import { Icon } from '../../components/Icon';
import { Portrait } from '../../components/Portrait';
import { clearMapLink, setMapLink } from '../../state/actions';
import { partyClass, partyOf } from '../../theme/parties';
import { deltaSr, deltaText, leaderPct, type RegionRowModel } from './model';

/** Origem do destaque no mapa pedido pela linha do Exterior (§9.11.4: só quem pediu limpa). */
export const REGION_MAP_LINK_SOURCE = 'regions';

/** Retrato do líder na linha: redondo de 26 px (§7.12 bloco 2). */
const PORTRAIT_PX = 26;

export interface RegionRowProps {
  model: RegionRowModel;
  /** Linha do Exterior como `<button>` (só com `features.abroad`, e nunca na TV): abre o Exterior. */
  onOpen?: () => void;
  /** `features.photos` (M26): sem fotos, iniciais no retrato. */
  photos?: boolean;
}

/**
 * Uma linha de "Por região" (§7.12 `RegionRow`): grade `1fr auto` × 2 (nome | líder; "{S} · faltam {Q}" | minibarra).
 * Mesmas classes para qualquer líder (só a de partido muda, N05/§16.6). Só a linha do Exterior é interativa: `<button>`
 * dentro do `<li>` com o nome de `regions.*.aria` (§15.4.2); hover e foco nela acendem o chip do Exterior no mapa (F104,
 * §9.11.4, `ZZ`), sem navegar. As linhas do Brasil não reagem a hover (§7.12 bloco 5).
 */
export function RegionRow({ model, onOpen, photos = true }: RegionRowProps) {
  const id = useId();
  const deltaId = `${id}-delta`;
  const interactive = onOpen !== undefined;
  const lead = model.leader;
  const party = lead ? partyOf(lead.candidate) : null;
  const delta = model.delta;

  // A linha pode sumir com o destaque aceso (clique que abre o Exterior desmonta o painel; troca de cena): limpa.
  useEffect(() => (interactive ? () => clearMapLink(REGION_MAP_LINK_SOURCE) : undefined), [interactive]);

  const body = (
    <>
      <span class="region-row__name">
        {model.code === 'EX' ? (
          <span class="region-row__globe">
            <Icon name="globe" size={12} />
          </span>
        ) : null}
        {model.name}
      </span>
      <span class="region-row__sub" title={model.subTitle ?? undefined}>
        {model.sub}
      </span>
      <span class="region-row__lead">
        {lead && party ? (
          <>
            <Portrait
              candidate={lead.candidate}
              photo={photos ? lead.candidate.photo : null}
              size={PORTRAIT_PX}
              shape="round"
            />
            <span class={`region-row__party ${partyClass(party)}`} data-dataviz="">
              {party.display || lead.candidate.party_code}
            </span>
            <AnimatedNumber value={lead.share} format={leaderPct} class="region-row__pct" />
            {delta ? (
              <span class="region-row__delta" title={delta.title} id={deltaId}>
                <AnimatedNumber value={delta.value} format={deltaText} srFormat={(v) => deltaSr(v, delta.party)} />
              </span>
            ) : null}
          </>
        ) : null}
      </span>
      <span class="region-row__bar">
        <Bar segments={lead && party ? [{ value: lead.share, party }] : []} rest="faint" fifty="mini" variant="mini" />
      </span>
    </>
  );

  const cls = ['region-row', model.code === 'EX' && 'region-row--abroad', lead === null && 'region-row--empty']
    .filter(Boolean)
    .join(' ');

  if (!interactive) {
    return (
      <li class={cls} data-testid="region-row" data-region={model.code}>
        <span class="region-row__grid">{body}</span>
      </li>
    );
  }

  const highlight = () => setMapLink({ kind: 'place', code: 'ZZ' }, REGION_MAP_LINK_SOURCE);
  const clear = () => clearMapLink(REGION_MAP_LINK_SOURCE);
  // §9.11.2: hover só de mouse (nunca no toque); o foco por teclado também acende (§9.11.4).
  const onEnter = (e: JSX.TargetedPointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse') highlight();
  };
  return (
    <li class={cls} data-testid="region-row" data-region={model.code}>
      <button
        type="button"
        class="region-row__grid region-row__button"
        aria-label={model.aria ?? undefined}
        aria-describedby={model.aria && delta ? deltaId : undefined}
        onClick={onOpen}
        onPointerEnter={onEnter}
        onPointerLeave={clear}
        onFocus={highlight}
        onBlur={clear}
      >
        {body}
      </button>
    </li>
  );
}
